import {
  EligibilityType,
  InsuranceType,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from 'react-query';
import {
  SchemaInsuranceVerificationAdapterServiceV1,
  SchemaInsuranceVerificationAdapterServiceV2,
  SchemaPersonV3Service,
} from '@frontend/schema';
import { GetQueryOptions, PayerVertical } from './types';

const queryKeys = {
  base: ['insurance-details'] as const,
  insuranceDetails: (locationId: string, personId: string) => [
    locationId,
    ...queryKeys.base,
    'insurance-details',
    personId,
  ],
  insuranceVerificationDetails: (
    locationId: string,
    personId: string,
    insuranceType: InsuranceType = InsuranceType.INSURANCE_TYPE_PRIMARY
  ) => [locationId, personId, insuranceType, ...queryKeys.base, 'insurance-verification-details'],
  nonDentalInsuranceVerificationDetails: (locationId: string, personId: string) => [
    locationId,
    personId,
    ...queryKeys.base,
    'non-dental-insurance-verification-details',
  ],
  insuranceSubscriberDetails: (locationId: string, personId: string, insuranceType: InsuranceType) => [
    locationId,
    insuranceType,
    ...queryKeys.base,
    'insurance-subscriber-details',
    personId,
  ],
  insuranceVerificationMetrics: (locationId: string, startDate: string, endDate: string) => [
    locationId,
    ...queryKeys.base,
    'insurance-verification-metrics',
    startDate,
    endDate,
  ],
  payerList: (locationId: string, vertical: string, eligibilityType: string) => [
    locationId,
    vertical,
    eligibilityType,
    ...queryKeys.base,
    'payer-list',
  ],
  providerList: (locationId: string) => [locationId, ...queryKeys.base, 'provider-list'],
  insurancePlans: (locationId: string, sourceId: string) => [
    locationId,
    ...queryKeys.base,
    'insurance-plans',
    sourceId,
  ],
  cachedDentalCoverageInfoByPersonId: (personId: string, locationId: string, insuranceType?: InsuranceType) => [
    personId,
    'cached-dental-coverage-info',
    locationId,
    insuranceType,
  ],
  payerConnectionDetails: (locationId: string) => [locationId, ...queryKeys.base, 'payer-connection-details'],
};

const LOCATION_ID_HEADER = 'Location-Id';
export const getLocationIdHeader = (locationId?: string) => ({
  headers: locationId ? { [LOCATION_ID_HEADER]: locationId } : undefined,
});
const logErrorInConsole = (actionName: string) => (err: unknown) =>
  console.error(`An error occurred while ${actionName}`, err);

export const useRefetchQueries = () => {
  const queryClient = useQueryClient();

  return {
    refetchGetInsuranceByPersonIdQuery: (locationId: string, personId: string) => {
      queryClient.refetchQueries({
        queryKey: queryKeys.insuranceVerificationDetails(locationId, personId),
        exact: true,
      });
    },
    refetchCachedDentalCoverageInfoByPersonId: (personId: string, locationId: string, insuranceType: InsuranceType) => {
      queryClient.refetchQueries({
        queryKey: queryKeys.cachedDentalCoverageInfoByPersonId(personId, locationId, insuranceType),
        exact: true,
      });
    },
  };
};

export const useInvalidateQueries = () => {
  const queryClient = useQueryClient();

  return {
    invalidateGetProviderListQuery: (locationId: string) => {
      queryClient.invalidateQueries(queryKeys.providerList(locationId));
    },
    invalidateGetInsuranceByPersonIdQuery: (personId: string, locationId: string) => {
      queryClient.invalidateQueries(queryKeys.insuranceDetails(locationId, personId));
      queryClient.invalidateQueries([personId, locationId, 'dental-coverage-info']);
    },
    invalidateGetInsuranceVerificationMetrics: (
      startDate: string | dayjs.Dayjs,
      endDate: string | dayjs.Dayjs,
      locationId: string
    ) => {
      const start = startDate ? dayjs(startDate).format('YYYY-MM-DD') : '';
      const end = endDate ? dayjs(endDate).add(1, 'day').format('YYYY-MM-DD') : '';
      queryClient.invalidateQueries(queryKeys.insuranceVerificationMetrics(locationId, start, end));
    },
    invalidateGetInsuranceVerificationDetailsPersonIdQuery: (
      personId: string,
      locationId: string,
      insuranceType: InsuranceType
    ) => {
      queryClient.invalidateQueries(queryKeys.insuranceVerificationDetails(locationId, personId, insuranceType));
    },
    invalidateGetNonDentalInsuranceVerificationDetailsByPersonIdQuery: (personId: string, locationId: string) => {
      queryClient.invalidateQueries(queryKeys.nonDentalInsuranceVerificationDetails(locationId, personId));
    },
    invalidateGetDentalInsuranceDetailsByPerson: (personId: string) => {
      queryClient.invalidateQueries([personId, 'dental-coverage-info', 'dental', 'dental-x-change']);
    },
  };
};

type UseGetInsuranceDetailsByPersonIdParams = {
  personId: string;
  locationId: string;
  insuranceType?: InsuranceType;
  options?: GetQueryOptions;
};

export const useGetInsuranceDetailsByPersonId = ({
  personId,
  locationId,
  options,
}: UseGetInsuranceDetailsByPersonIdParams) => {
  return useQuery({
    queryKey: queryKeys.insuranceDetails(locationId, personId),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV1.GetInsuranceByPersonID({ personId }, getLocationIdHeader(locationId)),
    enabled: !!(personId && locationId && options?.enabled),
    onError: logErrorInConsole('getting insurance details'),
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useGetDentalInsuranceVerificationDetailsPersonId = ({
  personId,
  locationId,
  insuranceType,
  options,
}: UseGetInsuranceDetailsByPersonIdParams) => {
  return useQuery({
    queryKey: queryKeys.insuranceVerificationDetails(locationId, personId, insuranceType!),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV1.GetInsuranceVerificationDetails(
        { personId, locationId, insuranceType },
        getLocationIdHeader(locationId)
      ),
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!(personId && locationId && options?.enabled),
    onError: logErrorInConsole('getting insurance verification details'),
  });
};

export const useGetNonDentalInsuranceVerificationDetailsByPersonId = ({
  personId,
  locationId,
  options,
}: UseGetInsuranceDetailsByPersonIdParams) => {
  return useQuery({
    queryKey: queryKeys.nonDentalInsuranceVerificationDetails(locationId, personId),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetInsuranceVerificationDetails(
        { personId },
        getLocationIdHeader(locationId)
      ),
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!(personId && locationId && options?.enabled),
    onError: logErrorInConsole('getting insurance verification details'),
  });
};

export const useGetSubscriberByDependentsPersonID = (
  personId: string,
  locationId: string,
  insuranceType: InsuranceType,
  options?: GetQueryOptions
) => {
  return useQuery({
    queryKey: queryKeys.insuranceSubscriberDetails(locationId, personId, insuranceType),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV1.GetSubscriberByDependentsPersonID(
        { personId },
        getLocationIdHeader(locationId)
      ),
    //As subscriber details are not integrated with PMS for secondary insurance we can't fetch subscriber details for secondary insurance type so disabling it
    enabled: Boolean(
      personId && locationId && !options?.disabled && insuranceType != InsuranceType.INSURANCE_TYPE_SECONDARY
    ),
    onError: logErrorInConsole('getting insurance subscriber details'),
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useGetInsuranceVerificationMetrics = (
  startDate: string | dayjs.Dayjs,
  endDate: string | dayjs.Dayjs,
  locationId: string,
  options?: GetQueryOptions
) => {
  const start = startDate ? dayjs(startDate).format('YYYY-MM-DD') : '';
  const end = endDate ? dayjs(endDate).add(1, 'day').format('YYYY-MM-DD') : '';

  return useQuery({
    queryKey: queryKeys.insuranceVerificationMetrics(locationId, start, end),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV1.GetVerificationMetrics(
        { start, end },
        getLocationIdHeader(locationId)
      ),
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(startDate && endDate && locationId && !options?.disabled),
    onError: logErrorInConsole('getting insurance verification metrics'),
  });
};

type UseGetPayerListParams = {
  locationId: string;
  vertical?: PayerVertical;
  eligibilityType?: EligibilityType;
  options?: GetQueryOptions;
};

export const useGetPayerList = ({
  locationId,
  vertical = PayerVertical.DENTAL,
  eligibilityType = EligibilityType.LITE,
  options,
}: UseGetPayerListParams) => {
  return useQuery({
    queryKey: queryKeys.payerList(locationId, vertical, eligibilityType),
    queryFn: async () => {
      const res = await SchemaInsuranceVerificationAdapterServiceV1.GetPayersList(
        {
          vertical,
          eligibilityType,
        },
        getLocationIdHeader(locationId)
      );
      return (
        res.payersResponse?.map((item) => ({
          id: item.id ?? '',
          name: item.name ?? '',
          alternatePayerIds: item.alternatePayerIds ?? [],
          websiteId: item?.websiteId || '',
        })) ?? []
      );
    },
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
    staleTime: 60 * 60 * 1000, // 1 hr
    enabled: Boolean(locationId && !options?.disabled),
    onError: logErrorInConsole('getting insurance payers'),
  });
};

export const useGetProviderList = (locationId: string, options?: GetQueryOptions) => {
  return useQuery({
    queryKey: queryKeys.providerList(locationId),
    queryFn: async () => {
      const res = await SchemaInsuranceVerificationAdapterServiceV1.GetProvidersList(
        {},
        getLocationIdHeader(locationId)
      );

      return (
        res.providersResponse?.map((item) => ({
          nationalProviderId: item.nationalProviderId ?? '',
          taxId: item.taxId ?? '',
        })) ?? []
      );
    },
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
    staleTime: 60 * 60 * 1000, // 1 hr
    enabled: Boolean(locationId && !options?.disabled),
    onError: logErrorInConsole('getting insurance providers'),
  });
};

export const useGetLocationNpis = (locationId: string, options?: GetQueryOptions) => {
  return useQuery({
    queryKey: queryKeys.providerList(locationId),
    queryFn: async () => {
      const res = await SchemaInsuranceVerificationAdapterServiceV2.ListLocationNpi(
        {}, // Empty object is passed since its a get request
        getLocationIdHeader(locationId)
      );

      return (
        res?.locationNpis?.map((item) => ({
          nationalProviderId: item.npi || '',
          taxId: item.taxId || '',
        })) ?? []
      );
    },
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
    staleTime: 60 * 60 * 1000, // 1 hr
    enabled: Boolean(locationId && options?.enabled),
    onError: logErrorInConsole('getting insurance providers'),
  });
};

export const useGetInsurancePlans = (sourceId: string, locationId: string, options?: GetQueryOptions) => {
  return useQuery({
    queryKey: queryKeys.insurancePlans(locationId, sourceId),
    queryFn: async () => {
      const res = await SchemaInsuranceVerificationAdapterServiceV1.GetInsurancePlans(
        { sourceId, locationId },
        getLocationIdHeader(locationId)
      );

      return res.plans ?? [];
    },
    retry: false,
    retryOnMount: false,
    cacheTime: Infinity,
    staleTime: 60 * 60 * 1000, // 1 hr
    enabled: Boolean(sourceId && locationId && !options?.disabled),
    onError: logErrorInConsole('getting insurance plans'),
  });
};

export const useGetCachedDentalCoverageInfoByPersonId = (
  personId: string,
  locationId: string,
  insuranceType: InsuranceType = InsuranceType.INSURANCE_TYPE_PRIMARY,
  isHttpReady = true
) => {
  return useQuery({
    queryKey: queryKeys.cachedDentalCoverageInfoByPersonId(personId, locationId, insuranceType),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetCachedDentalCoverageInfoByPersonId(
        { personId, insuranceType },
        getLocationIdHeader(locationId)
      ),
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!(personId && locationId && isHttpReady),
    onError: logErrorInConsole('getting insurance verification details'),
  });
};

export const useGetHouseholdData = (locationId: string, householdId: string, insuranceType: InsuranceType) => {
  return useQuery({
    queryKey: ['insurance', 'household-patient-details', locationId, householdId, insuranceType],
    queryFn: () =>
      SchemaPersonV3Service.SearchPersonsLegacy({
        locationIds: [locationId],
        householdId: householdId,
        page: { size: 50 },
      }),
    //As subscriber details are not integrated with PMS for secondary insurance we can't fetch household data for secondary insurance type so disabling it
    enabled: !!locationId && !!householdId && insuranceType != InsuranceType.INSURANCE_TYPE_SECONDARY,
    retry: false,
    retryOnMount: false,
  });
};
export const useGetPayerConnectionDetails = (locationId: string, options?: GetQueryOptions) => {
  return useQuery({
    queryKey: queryKeys.payerConnectionDetails(locationId),
    queryFn: () => SchemaInsuranceVerificationAdapterServiceV2.GetPayerConnectionDetailsByLocationId({ locationId }),
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!(locationId && options?.enabled),
    onError: logErrorInConsole('getting payer connection details'),
  });
};
